import Swiper, { Navigation, Pagination, Scrollbar, Autoplay } from 'swiper';

const wrapChildrenInSlides = (container) => {
  // Find the element that will contain slides (usually .swiper-wrapper)
  const swiperWrapper = container.querySelector('.swiper-wrapper');

  if (!swiperWrapper) {
    console.warn('No .swiper-wrapper found in:', container);
    return;
  }

  swiperWrapper.removeAttribute('data-promo-tracking');
  swiperWrapper.removeAttribute('data-promo-title');
  swiperWrapper.removeAttribute('data-promo-creative');

  // Get all direct children that aren't navigation elements
  const childrenToWrap = Array.from(swiperWrapper.children).filter((child) => {
    // Skip elements that are already slides or are part of Swiper's UI
    return (
      !child.classList.contains('swiper-slide') &&
      !child.classList.contains('swiper-button-next') &&
      !child.classList.contains('swiper-button-prev') &&
      !child.classList.contains('swiper-pagination')
    );
  });

  // Wrap each child in a swiper-slide div
  childrenToWrap.forEach((child) => {
    const slideDiv = document.createElement('div');
    slideDiv.className = 'swiper-slide';

    // Move the child into the new slide div
    swiperWrapper.insertBefore(slideDiv, child);
    slideDiv.appendChild(child);
  });
};

const updateArrowTopPosition = (swiper) => {
  const swiperElement = swiper.$el[0];
  const imageSelector = '.product-rec-c-carousel__product-card-images, .product-card-c-basis__images';

  const images = swiperElement.querySelectorAll(imageSelector);
  if (images.length === 0) {
    return;
  }

  // Find ProductTile images within the swiper
  function updateProductTileImageHeight() {
    const productTileImages = swiperElement.querySelectorAll(imageSelector);
    if (productTileImages.length === 0) return;
    // Use the first fully loaded image to set the height
    for (let i = 0; i < productTileImages.length; i++) {
      const img = productTileImages[i];
      if (img.offsetHeight > 0) {
        // Set the CSS variable to half the image height on the parent Shopify block element
        const halfHeight = Math.round(img.offsetHeight / 2);
        swiperElement.parentNode.style.setProperty('--product-image-height', `${halfHeight}px`);
        break;
      }
    }
  }

  // Initialize height once images are loaded
  updateProductTileImageHeight();

  // Use ResizeObserver to update measurements when window resizes (more efficient than resize event)
  const resizeObserver = new ResizeObserver(() => {
    updateProductTileImageHeight();
  });
  resizeObserver.observe(swiperElement);
};

// Set up custom navigation event listeners for a swiper instance
const setupCustomNavigation = (element, swiperInstance) => {
  const prevButtons = element.nextElementSibling.querySelectorAll('.swiper-prev');
  const nextButtons = element.nextElementSibling.querySelectorAll('.swiper-next');

  prevButtons.forEach((button) => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      swiperInstance.slidePrev();
    });
  });

  nextButtons.forEach((button) => {
    button.addEventListener('click', (e) => {
      e.preventDefault();
      swiperInstance.slideNext();
    });
  });

  // Initial button state update
  updateNavigationButtonsState(swiperInstance, prevButtons, nextButtons);

  // Add event listeners to update button states on slide changes
  swiperInstance.on('slideChange', () => {
    updateNavigationButtonsState(swiperInstance, prevButtons, nextButtons);
  });
};

// Function to update navigation button states based on current slide position
const updateNavigationButtonsState = (swiper, prevButtons, nextButtons) => {
  const isBeginning = swiper.isBeginning;
  prevButtons.forEach((button) => {
    if (isBeginning) {
      button.classList.add('swiper-button-disabled');
      button.setAttribute('disabled', 'disabled');
    } else {
      button.classList.remove('swiper-button-disabled');
      button.removeAttribute('disabled');
    }
  });

  const isEnd = swiper.isEnd;
  nextButtons.forEach((button) => {
    if (isEnd) {
      button.classList.add('swiper-button-disabled');
      button.setAttribute('disabled', 'disabled');
    } else {
      button.classList.remove('swiper-button-disabled');
      button.removeAttribute('disabled');
    }
  });
};

const Carousel = () => {
  document.querySelectorAll('[swiper]').forEach((element) => {
    // Skip if already initialized
    if (element.querySelector('.swiper')?.classList.contains('swiper-initialized')) {
      return;
    }

    const configScript = element.querySelector('script[type="swiper/config"]');
    if (!configScript) {
      console.warn('No Swiper config found for element:', element);
      return;
    }

    try {
      const config = JSON.parse(configScript.textContent.replace(/\s+/g, ' '));

      // Wrap children in swiper-slide divs before initializing
      wrapChildrenInSlides(element);

      const swiperConfig = {
        modules: [Navigation, Pagination, Scrollbar, Autoplay],
        init: true,
        autoHeight: false,
        loop: false,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        a11y: {
          enabled: true,
          containerMessage: 'Carousel',
          prevSlideMessage: 'Previous block',
          nextSlideMessage: 'Next block',
          firstSlideMessage: 'This is the first block',
          lastSlideMessage: 'This is the last block',
          paginationBulletMessage: 'Go to block {{index}}',
        },
        keyboard: {
          enabled: true,
        },
        on: {
          imagesReady: (swiper) => {
            updateArrowTopPosition(swiper);
          },
          init: (swiper) => {
            setupCustomNavigation(element, swiper);
          },
        },
        ...config,
      };

      new Swiper(element, swiperConfig);
    } catch (error) {
      console.error('Error initializing carousel:', error);
    }
  });
};

export default Carousel;
